<script lang="ts">
  import { routes } from "$lib/routes";
  import { SVG } from "$lib/components";
  import investigationImg from "./investigation.svg?raw";
  import { onMount } from "svelte";
  import { cn } from "$lib/utility";

  interface CalNamespace {
    q: any[];
    [key: string]: any;
    (action: string, config: Record<string, any>): void;
  }

  interface CalInstance {
    loaded?: boolean;
    ns: Record<string, CalNamespace>;
    q: any[];
    (action: string, ...args: any[]): void;
  }

  interface WindowWithCal extends Window {
    Cal?: CalInstance;
  }

  let lastEventHash = $state("");
  let lastEventTime = $state(0);

  const { isAccountActivated = true } = $props<{ isAccountActivated?: boolean }>();

  let showPopup = $state(false);
  let isVisible = $state(false);

  let calEventListenerAdded = $state(false);

  function addCalEventListener() {
    if (!calEventListenerAdded) {
      calEventListenerAdded = true;
      window.addEventListener("message", function (e) {
        const eventData = e.data.data;
        const eventType = `${e.data.type}`;
        const now = Date.now();
        if (eventType === lastEventHash && now - lastEventTime < 1000) {
          return;
        }
        lastEventHash = eventType;
        lastEventTime = now;

        if (
          e.data &&
          typeof e.data === "object" &&
          e.data.originator === "CAL" &&
          e.data.type === "bookingSuccessfulV2"
        ) {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: "cal_booking_completed",
              booking_id: eventData?.booking?.id || eventData?.uid,
              booking_title: eventData?.booking?.title || eventData?.title,
              booking_date: eventData?.date || eventData?.startTime,
              event_type_id: eventData?.eventType?.id || eventData?.eventTypeId,
            });
          } else {
            console.warn("Google Tag Manager dataLayer not found");
          }
        }
      });
    }
  }

  onMount(() => {
    const hasSkippedOnboarding = document.cookie.split("; ").find((row) => row.startsWith("onboarding_skipped="));

    showPopup = !isAccountActivated && !hasSkippedOnboarding;

    if (showPopup) {
      setTimeout(() => {
        isVisible = true;
      }, 50);
    }

    // Initialize Cal.com script
    if (typeof window !== "undefined") {
      (function (C, A, L) {
        let p = function (a: { (): void; q: any }, ar: IArguments | string[]) {
          a.q.push(ar);
        };
        let d = C.document;
        C.Cal =
          C.Cal ||
          function () {
            let cal = C.Cal;
            let ar = arguments;
            if (!cal.loaded) {
              cal.ns = {};
              cal.q = cal.q || [];
              d.head.appendChild(d.createElement("script")).src = A;
              cal.loaded = true;
            }
            if (ar[0] === L) {
              interface CalApi {
                (): void;
                q: unknown[];
              }
              const api: CalApi = function () {
                p(api, arguments);
              };
              const namespace = ar[1];
              api.q = api.q || [];
              if (typeof namespace === "string") {
                cal.ns[namespace] = cal.ns[namespace] || api;
                p(cal.ns[namespace], ar);
                p(cal, ["initNamespace", namespace]);
              } else p(cal, ar);
              return;
            }
            p(cal, ar);
          };
      })(window, "https://app.cal.com/embed/embed.js", "init");

      window.Cal("init", "quick-demo", { origin: "https://cal.com" });
      window.Cal.ns["quick-demo"]("ui", { hideEventTypeDetails: false, layout: "month_view" });

      addCalEventListener();
    }
  });

  function skipOnboarding() {
    isVisible = false;
    setTimeout(() => {
      const expiryDate = new Date();
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      document.cookie = `onboarding_skipped=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict; Secure`;
      showPopup = false;
    }, 300);
  }
</script>

{#if showPopup}
  <div
    class={cn(
      "fixed inset-0 z-[999] flex items-center justify-center dark:bg-white bg-black/10 backdrop-blur-sm dark:md:bg-white/5 transition-opacity duration-300 ease-in-out",
      isVisible ? "opacity-100" : "opacity-0",
    )}
  >
    <div
      class={cn(
        "relative w-[600px] border border-white/10 bg-white p-12 shadow-2xl dark:bg-theme-background-tertiary dark:text-theme-text-primary transition-all duration-300 ease-in-out mt-16 md:mt-0",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
      )}
    >
      <div class="absolute -top-20 left-1/2 -translate-x-1/2 -z-10">
        <div
          class="w-[180px] h-[180px] rounded-full border border-white/10 bg-white dark:bg-theme-background-tertiary"
        ></div>
      </div>

      <div class="absolute -top-20 left-1/2 -translate-x-1/2">
        <div
          class="w-[180px] h-[180px] rounded-full flex items-center justify-center bg-white dark:bg-theme-background-tertiary"
        >
          <SVG file={investigationImg} className="p-8 text-theme-text-secondary" />
        </div>
      </div>

      <h1 class="mb-6 mt-16 text-center text-4xl font-bold">Welcome to Chatsheet!</h1>

      <p class="mb-10 text-xl leading-relaxed text-center dark:text-theme-text-secondary">
        We'd love to give you a personal onboarding to help you get the most out of Chatsheet. Schedule a quick call
        with our team to get started.
      </p>

      <div class="mb-10">
        <button
          onclick={skipOnboarding}
          data-cal-link="team/chatsheet/quick-demo"
          data-cal-namespace="quick-demo"
          data-cal-config={{ layout: "month_view" }}
          class="transition-default w-full h-12 py-3 text-center bg-theme-green border border-black text-black hover:bg-theme-accent-hover"
        >
          Schedule onboarding call
        </button>
      </div>

      <button
        onclick={skipOnboarding}
        class="transition-default absolute bottom-3 right-3 text-xs text-gray-400 hover:text-gray-600 dark:text-theme-text-secondary/50 dark:hover:text-theme-text-secondary"
      >
        Skip for now
      </button>
    </div>
  </div>
{/if}
